// OWNER: LG-ADMIN. §10 tab 1 and §6.2: the profiles table, New niche (propose with Opus 5 -> edit chips -> approve),
// Smoke / Pilot shortcuts into New search, and Activate (409 activation_blocked lists every gate). A failed smoke says
// which checks failed (from its stored SmokeReport) and links to the full report on the Results tab.
import { useState } from 'preact/hooks';
import type { Country } from '../../../src/shared/api';
import { looksHealthcare } from '../../../src/shared/api-core';
import {
  LEAD_OFFERS, type BuyerMode, type LeadOffer, type NicheProfile, type NicheProfileRow, type ProposalDrop, type SmokeReport,
} from '../../../src/shared/api-leadgen';
import { navigate } from '../app';
import { Empty, ErrorBanner, fmtDateTime, toast } from '../components/ui';
import { ApiErrorBox, Check, ConfirmModal, Field, ProblemList, RowMenu, inputValue, useLoad, useUnsavedWarning, type MenuItem } from '../admin/ui';
import { apiErrorView, type ApiErrorView } from '../admin/errors';
import { refreshApprovals, useApprovals } from '../approvals';
import { leadgenApi } from './endpoints';
import {
  ACTIVATION_GATE_LABELS, CRTC_SENTENCE, MODE_RULES, NICHE_TONES, OFFER_LABELS, blockersFromError, deepLinkPath, failedSmokeIds, fastActivateText,
  gatesFromError, isMonthCapBlocker, isSmokeReport, newSearchLink, parseSmokeCity, smokeBatchPlan, smokeCandidates, smokeSummary,
} from './logic';
import { CHIP_FIELDS, LICENCE_ADAPTERS, VENDOR_EFFECTS, cloneProfile, profileJson, profileProblems } from './profile';
import { ChipEditor, ExtLink, ToneChip, useElapsed } from './parts';

export function NichesTab(_props: Record<string, never>) {
  const niches = useLoad(() => leadgenApi.niches(), []);
  const [editing, setEditing] = useState<{ row: NicheProfileRow; dropped: ProposalDrop[] } | null>(null);
  const [proposing, setProposing] = useState(false);
  const [gates, setGates] = useState<{ row: NicheProfileRow; gates: ReturnType<typeof gatesFromError>; message: string } | null>(null);
  const [fast, setFast] = useState<NicheProfileRow | null>(null);
  const appr = useApprovals();
  const isOwner = !!appr?.isOwner;
  // a failed smoke must say why: load the stored report of each failed smoke (a handful at most)
  const failedIds = failedSmokeIds(niches.data ?? []);
  const smokeReports = useLoad(async () => {
    const out: Record<number, SmokeReport | 'none' | 'error'> = {};
    await Promise.all(failedIds.map(async (id) => {
      try { const d = await leadgenApi.search(id); out[id] = isSmokeReport(d.search.report) ? d.search.report : 'none'; }
      catch { out[id] = 'error'; }
    }));
    return out;
  }, [failedIds.join(',')]);

  if (editing) {
    return (
      <NicheEditor row={editing.row} dropped={editing.dropped}
        onBack={() => { setEditing(null); void niches.reload(); }}
        onSaved={(row) => { setEditing({ row, dropped: [] }); void niches.reload(); }} />
    );
  }

  const rows = [...(niches.data ?? [])].sort((a, b) => a.displayName.localeCompare(b.displayName) || a.key.localeCompare(b.key) || b.version - a.version);
  const newest = new Map<string, number>();
  for (const r of rows) newest.set(r.key, Math.max(newest.get(r.key) ?? 0, r.version));

  const approve = async (r: NicheProfileRow) => {
    try { await leadgenApi.approveNiche(r.key, r.version); toast(`${r.displayName} v${r.version} approved. Next: a smoke test.`, 'ok'); await niches.reload(); }
    catch (e) { toast(apiErrorView(e).message, 'bad'); }
  };
  const activate = async (r: NicheProfileRow) => {
    try { await leadgenApi.activateNiche(r.key, r.version); toast(`${r.displayName} v${r.version} is active. Full searches are unlocked.`, 'ok'); await niches.reload(); }
    catch (e) {
      const g = gatesFromError(e);
      if (g.length) setGates({ row: r, gates: g, message: apiErrorView(e).message });
      else toast(apiErrorView(e).message, 'bad');
    }
  };
  const menu = (r: NicheProfileRow): MenuItem[] => {
    const items: MenuItem[] = [{ label: r.status === 'draft' ? 'Edit' : 'Open (edits make a new draft)', onSelect: () => setEditing({ row: r, dropped: [] }) }];
    if (r.status === 'draft') items.push({ label: 'Approve', onSelect: () => void approve(r) });
    if (r.status === 'approved' || r.status === 'active') {
      items.push({ label: 'Smoke test…', separatorBefore: true, onSelect: () => navigate(newSearchLink(r.key, r.version, 'smoke')) });
      if (r.smokePassed) items.push({ label: 'Pilot…', onSelect: () => navigate(newSearchLink(r.key, r.version, 'pilot')) });
    }
    if (r.status === 'approved') items.push({ label: 'Activate', onSelect: () => void activate(r) });
    // Owner fast path: runnable now, without the hours a smoke, a pilot and 30 checks take (server: owner only).
    if (isOwner && (r.status === 'draft' || r.status === 'approved') && newest.get(r.key) === r.version) {
      items.push({ label: 'Activate now (skip smoke and pilot)…', onSelect: () => setFast(r) });
    }
    if (r.status === 'active') items.push({ label: 'Full search…', onSelect: () => navigate(newSearchLink(r.key, r.version, 'full')) });
    if (r.smokeSearchId) items.push({ label: `Smoke report and results (#${r.smokeSearchId})`, separatorBefore: true, onSelect: () => navigate(deepLinkPath('results', r.smokeSearchId)) });
    if (r.pilotSearchId) items.push({ label: `Pilot report and results (#${r.pilotSearchId})`, onSelect: () => navigate(deepLinkPath('results', r.pilotSearchId)) });
    return items;
  };

  return (
    <div class="stack" style="gap:12px">
      <div class="adm-toolbar">
        <span class="lg-note grow">Propose → approve → smoke → pilot → activate. Full searches need an active profile.{isOwner ? ' As the owner you can also activate a niche right away (Manage → Activate now).' : ''}</span>
        <button class="btn btn-primary" onClick={() => setProposing(true)} disabled={proposing}>New niche</button>
      </div>
      {niches.data ? <SmokeBatch rows={niches.data} onDone={() => void niches.reload()} /> : null}
      {proposing ? (
        <ProposeCard onCancel={() => setProposing(false)}
          onProposed={(res) => { setProposing(false); setEditing({ row: res.niche, dropped: res.dropped }); void niches.reload(); }} />
      ) : null}
      <ErrorBanner error={niches.error} />
      {niches.loading && !niches.data ? <div class="muted">Loading profiles…</div> : null}
      {niches.data && rows.length === 0 ? <Empty>No niche profiles yet. Propose one with New niche.</Empty> : null}
      {rows.length ? (
        <div class="adm-table-wrap">
          <table>
            <thead><tr>
              <th>Niche</th><th class="num">Version</th><th>Status</th><th>Smoke</th><th>Pilot precision</th><th>Detectors passed</th><th>Proposed by</th><th>Approved</th><th />
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr class={newest.get(r.key) !== r.version ? 'adm-dim' : ''}>
                  <td><b>{r.displayName}</b><span class="adm-cell-sub lg-mono">{r.key}</span></td>
                  <td class="num">v{r.version}</td>
                  <td><ToneChip tone={NICHE_TONES[r.status]}>{r.status}</ToneChip></td>
                  <td>{r.smokePassed === null ? <span class="muted">none yet</span> : (
                    <>
                      {r.smokeSearchId ? (
                        <a href={deepLinkPath('results', r.smokeSearchId)} title={`Open the smoke report (search #${r.smokeSearchId})`}
                          onClick={(e) => { e.preventDefault(); navigate(deepLinkPath('results', r.smokeSearchId)); }}>
                          <ToneChip tone={r.smokePassed ? 'ok' : 'bad'}>{r.smokePassed ? 'passed' : 'failed'}</ToneChip>
                        </a>
                      ) : <ToneChip tone={r.smokePassed ? 'ok' : 'bad'}>{r.smokePassed ? 'passed' : 'failed'}</ToneChip>}
                      {r.smokePassed === false && r.smokeSearchId ? <SmokeWhy report={smokeReports.data?.[r.smokeSearchId]} loading={smokeReports.loading} /> : null}
                    </>
                  )}</td>
                  <td>{r.pilotPrecision === null ? <span class="muted">not measured</span>
                    : <ToneChip tone={r.pilotPrecision >= 0.85 ? 'ok' : 'bad'} title="In-niche precision on the pilot's hand checks; activation needs ≥ 0.85 on n ≥ 30.">{r.pilotPrecision.toFixed(2)}</ToneChip>}</td>
                  <td>{r.detectors.total ? `${r.detectors.passed} of ${r.detectors.total} passed` : <span class="muted">none checked</span>}</td>
                  <td class="lg-mono">{r.proposedBy}</td>
                  <td>{r.approvedAt ? fmtDateTime(r.approvedAt) : '—'}{r.activatedAt ? <span class="adm-cell-sub">active {fmtDateTime(r.activatedAt)}</span> : null}</td>
                  <td><RowMenu label="Manage" ariaLabel={`Manage ${r.displayName} v${r.version}`} items={menu(r)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {fast ? (
        <ConfirmModal title={`Activate ${fast.displayName} now?`} confirmLabel="Activate now" body={fastActivateText(fast)}
          onClose={() => setFast(null)}
          onConfirm={async () => {
            const r = await leadgenApi.activateNiche(fast.key, fast.version, { ownerOverride: true });
            toast(`${r.displayName} v${r.version} is active${r.activationOverride?.waived?.length ? ` (waived: ${r.activationOverride.waived.map((k) => ACTIVATION_GATE_LABELS[k] ?? k).join(', ')})` : ''}. Full searches are unlocked.`, 'ok');
            setFast(null);
            await niches.reload();
          }} />
      ) : null}
      {gates ? (
        <div class="card stack" role="alert">
          <div class="lg-card-head"><h3>{gates.row.displayName} v{gates.row.version} can't be activated yet</h3><button class="btn btn-sm" onClick={() => setGates(null)}>Close</button></div>
          <div class="lg-note">{gates.message}</div>
          <table>
            <tbody>
              {gates.gates.map((g) => (
                <tr><td style="width:36px">{g.ok ? <ToneChip tone="ok">✓</ToneChip> : <ToneChip tone="bad">✗</ToneChip>}</td>
                  <td><b>{ACTIVATION_GATE_LABELS[g.key] ?? g.key}</b><span class="adm-cell-sub" style="white-space:normal">{g.detail}</span></td></tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

type SmokeOutcome = { key: string; name: string; text: string; tone: 'ok' | 'info' | 'bad' | 'warn'; searchId: number | null };

/** P0-5: smoke many niches in one city: create → estimate → run per niche, one after another (a non-owner's run becomes a
 *  spend request). Stops at the first month-cap blocker; every other refusal is shown per niche. */
function SmokeBatch(props: { rows: NicheProfileRow[]; onDone: () => void }) {
  const candidates = smokeCandidates(props.rows);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [city, setCity] = useState('Atlanta, GA');
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState<SmokeOutcome[]>([]);
  const parsed = parseSmokeCity(city);
  const country: Country | null = 'problem' in parsed ? null : parsed.country;
  const geo = useLoad(async () => (country ? leadgenApi.geo(country) : null), [country]);
  if (!candidates.length) return null;
  const selected = candidates.filter((r) => picked.has(r.key));
  const plan = smokeBatchPlan(selected, city, geo.data ?? null);
  const toggle = (key: string, on: boolean) => setPicked((cur) => { const n = new Set(cur); if (on) n.add(key); else n.delete(key); return n; });

  const run = async () => {
    setBusy(true); setOut([]);
    const results: SmokeOutcome[] = [];
    const push = (o: SmokeOutcome) => { results.push(o); setOut([...results]); };
    for (const it of plan.items) {
      const name = `${it.row.displayName} v${it.row.version}`;
      if (!it.request) { push({ key: it.row.key, name, text: it.problem ?? 'Not runnable.', tone: 'bad', searchId: null }); continue; }
      try {
        const search = await leadgenApi.createSearch(it.request);
        const est = await leadgenApi.estimate(search.id);
        if (est.blockers.length) {
          push({ key: it.row.key, name, text: est.blockers.join(' '), tone: 'bad', searchId: search.id });
          if (est.blockers.some(isMonthCapBlocker)) break;
          continue;
        }
        const r = await leadgenApi.run(search.id, { budgetUsd: MODE_RULES.smoke.budgetUsd ?? 2, confirmHandPick: false });
        if ('message' in r) push({ key: it.row.key, name, text: r.message, tone: 'info', searchId: search.id });
        else push({ key: it.row.key, name, text: `Running (search #${r.id}, up to $${r.budgetUsd.toFixed(2)}).`, tone: 'ok', searchId: r.id });
      } catch (e) {
        const b = blockersFromError(e);
        push({ key: it.row.key, name, text: b.length ? b.join(' ') : apiErrorView(e).message, tone: 'bad', searchId: null });
        if ([...b, apiErrorView(e).message].some(isMonthCapBlocker)) break;
      }
    }
    void refreshApprovals();
    setBusy(false);
    setPicked(new Set());
    props.onDone();
  };

  return (
    <div class="card stack">
      <div class="lg-card-head">
        <h3>Smoke several niches</h3>
        <span class="lg-note">Each smoke: one city, every term, 3 places per term, up to $2. Runs one after another.</span>
      </div>
      <div class="lg-smoke-pick">
        {candidates.map((r) => (
          <Check checked={picked.has(r.key)} onChange={(v) => toggle(r.key, v)} disabled={busy}
            label={<>{r.displayName} <span class="lg-note">v{r.version} · {r.smokePassed === false ? 'smoke failed' : 'no smoke yet'}</span></>} />
        ))}
      </div>
      <div class="row" style="flex-wrap:wrap;gap:8px">
        <Field label="City">
          <input value={city} onInput={(e) => setCity(inputValue(e))} placeholder="Atlanta, GA" style="max-width:200px" disabled={busy} />
        </Field>
        <button class="btn btn-primary" disabled={busy || !selected.length || !!plan.problem} onClick={() => void run()}>
          {busy ? 'Starting smokes…' : `Smoke selected (${selected.length}) in ${'problem' in parsed ? '…' : `${parsed.city}, ${parsed.abbr}`}`}
        </button>
      </div>
      {plan.problem && selected.length ? <div class="lg-note lg-err-line">{plan.problem}</div> : null}
      {!busy && !out.length && plan.items.some((i) => i.problem) && !plan.problem ? (
        <ul class="adm-problems">{plan.items.filter((i) => i.problem).map((i) => <li class="error">{i.row.displayName}: {i.problem}</li>)}</ul>
      ) : null}
      {out.length ? (
        <table>
          <tbody>
            {out.map((o) => (
              <tr><td><b>{o.name}</b></td><td><ToneChip tone={o.tone}>{o.tone === 'ok' ? 'running' : o.tone === 'info' ? 'request sent' : 'not started'}</ToneChip></td>
                <td style="white-space:normal">{o.text}{o.searchId ? <> · <a href={deepLinkPath('searches', o.searchId)} onClick={(e) => { e.preventDefault(); navigate(deepLinkPath('searches', o.searchId)); }}>#{o.searchId}</a></> : null}</td></tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}

/** Under a failed smoke: "10 of 11 checks passed; failed: charge cap", or why the report can't be shown. */
function SmokeWhy(props: { report: SmokeReport | 'none' | 'error' | undefined; loading: boolean }) {
  const r = props.report;
  const text = r === undefined ? (props.loading ? 'loading the report…' : 'report not loaded')
    : r === 'error' ? "couldn't load the report" : r === 'none' ? 'no report stored' : smokeSummary(r).text;
  return <span class="adm-cell-sub lg-err-line lg-smoke-why">{text}</span>;
}

// ---------------------------------------------------------------- propose

function ProposeCard(props: { onCancel: () => void; onProposed: (res: { niche: NicheProfileRow; dropped: ProposalDrop[] }) => void }) {
  const [text, setText] = useState('');
  const [country, setCountry] = useState<Country>('US');
  const [offerHint, setOfferHint] = useState<LeadOffer | ''>('');
  const [started, setStarted] = useState<number | null>(null);
  const [error, setError] = useState<ApiErrorView | null>(null);
  const elapsed = useElapsed(started);
  const crtc = country === 'CA' && looksHealthcare(text);
  const go = async () => {
    setStarted(Date.now()); setError(null);
    try { props.onProposed(await leadgenApi.proposeNiche({ text: text.trim(), country, ...(offerHint ? { offerHint } : {}) })); }
    catch (e) { setError(apiErrorView(e)); }
    finally { setStarted(null); }
  };
  return (
    <div class="card stack">
      <div class="lg-card-head"><h3>New niche</h3><span class="lg-note">Claude Opus 5 drafts the profile (about 10 s, about $0.10, estimate). You edit and approve it.</span></div>
      <div class="adm-grid">
        <Field label="Niche" hint="In plain words, e.g. garage door repair"><input value={text} maxLength={120} onInput={(e) => setText(inputValue(e))} disabled={started !== null} /></Field>
        <Field label="Country">
          <select value={country} onChange={(e) => setCountry(inputValue(e) as Country)} disabled={started !== null}><option value="US">US</option><option value="CA">Canada</option></select>
        </Field>
        <Field label="Offer hint (optional)">
          <select value={offerHint} onChange={(e) => setOfferHint(inputValue(e) as LeadOffer | '')} disabled={started !== null}>
            <option value="">No hint</option>
            {LEAD_OFFERS.map((o) => <option value={o}>{OFFER_LABELS[o]}</option>)}
          </select>
        </Field>
      </div>
      {crtc ? <div class="chip chip-bad" style="display:block;padding:8px 10px;white-space:normal">{CRTC_SENTENCE}</div> : null}
      <ApiErrorBox error={error} />
      <div class="row" style="justify-content:flex-end">
        <button class="btn" onClick={props.onCancel} disabled={started !== null}>Cancel</button>
        <button class="btn btn-primary" onClick={go} disabled={started !== null || text.trim().length < 3 || crtc}>
          {started !== null ? `Proposing… ${elapsed}s` : 'Propose'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- editor

const BUYER_MODES: BuyerMode[] = ['emergency', 'scheduled', 'considered'];

function NicheEditor(props: { row: NicheProfileRow; dropped: ProposalDrop[]; onBack: () => void; onSaved: (row: NicheProfileRow) => void }) {
  const { row } = props;
  const [p, setP] = useState<NicheProfile>(() => cloneProfile(row.profile));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiErrorView | null>(null);
  const dirty = profileJson(p) !== profileJson(row.profile);
  useUnsavedWarning(dirty);
  const problems = profileProblems(p);
  const blocking = problems.some((x) => x.level === 'error');
  const set = <K extends keyof NicheProfile>(k: K, v: NicheProfile[K]) => setP((cur) => ({ ...cur, [k]: v }));

  const save = async () => {
    setBusy(true); setError(null);
    try {
      const saved = await leadgenApi.updateNiche(row.key, row.version, { displayName: p.display_name.trim(), profile: { ...p, display_name: p.display_name.trim() } });
      toast(saved.version !== row.version ? `Saved as ${saved.displayName} v${saved.version} (draft). Approve it, then smoke and pilot again.` : 'Saved.', 'ok');
      props.onSaved(saved);
    } catch (e) { setError(apiErrorView(e)); } finally { setBusy(false); }
  };
  const approve = async () => {
    setBusy(true); setError(null);
    try { const r = await leadgenApi.approveNiche(row.key, row.version); toast(`${r.displayName} v${r.version} approved. Next: a smoke test.`, 'ok'); props.onSaved(r); }
    catch (e) { setError(apiErrorView(e)); } finally { setBusy(false); }
  };
  const back = () => { if (!dirty || confirm('Leave without saving your edits?')) props.onBack(); };

  const moveOffer = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= p.offers_ranked.length) return;
    const next = [...p.offers_ranked];
    [next[i], next[j]] = [next[j], next[i]];
    set('offers_ranked', next);
  };
  const unusedOffers = LEAD_OFFERS.filter((o) => !p.offers_ranked.some((x) => x.offer === o));

  return (
    <div class="lg-prof">
      <div><a href="#" onClick={(e) => { e.preventDefault(); back(); }}>← All niches</a></div>
      <div class="adm-head">
        <div>
          <div class="row"><h2 style="margin:0">{p.display_name || row.displayName}</h2><ToneChip tone={NICHE_TONES[row.status]}>{row.status}</ToneChip></div>
          <div class="adm-sub"><span class="lg-mono">{row.key}</span> · v{row.version} · proposed by {row.proposedBy} · {fmtDateTime(row.createdAt)}</div>
        </div>
        <div class="row">
          {row.status === 'draft' ? <button class="btn" onClick={approve} disabled={busy || dirty || blocking} title={dirty ? 'Save first' : undefined}>Approve</button> : null}
          <button class="btn btn-primary" onClick={save} disabled={busy || !dirty || blocking}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
      {row.status !== 'draft' ? <div class="adm-muted-box">This version is {row.status}. Saving an edit creates v{row.version + 1} as a draft; it needs approval, a smoke and a pilot before Full searches use it.</div> : null}
      {props.dropped.length ? (
        <div class="card stack">
          <h3 style="margin:0">Removed by the checks ({props.dropped.length})</h3>
          <table><thead><tr><th>Field</th><th>Value</th><th>Why</th></tr></thead>
            <tbody>{props.dropped.map((d) => <tr><td class="lg-mono">{d.field}</td><td>{d.value}</td><td>{d.reason}</td></tr>)}</tbody></table>
        </div>
      ) : null}
      <ProblemList problems={problems} />
      <ApiErrorBox error={error} />

      <div class="card lg-prof-sec">
        <h3>Basics</h3>
        <div class="adm-grid">
          <Field label="Display name"><input value={p.display_name} onInput={(e) => set('display_name', inputValue(e))} /></Field>
          <Field label="Buyer mode">
            <select value={p.buyer_mode} onChange={(e) => set('buyer_mode', inputValue(e) as BuyerMode)}>{BUYER_MODES.map((m) => <option value={m}>{m}</option>)}</select>
          </Field>
          <Field label="Rank term" hint="Map rank keywords: “<term> near me” and “<city> <term>”"><input value={p.rank_term} onInput={(e) => set('rank_term', inputValue(e))} /></Field>
          <Field label="Weekday close threshold" hint="24-hour, e.g. 18:00">
            <input value={p.hours_norm.weekday_close_threshold} onInput={(e) => set('hours_norm', { ...p.hours_norm, weekday_close_threshold: inputValue(e).trim() })} />
          </Field>
          <Field label="Review ceiling" hint="Drop places above this many reviews (too big). Empty = none.">
            <input type="number" min={1} value={p.review_ceiling ?? ''} onInput={(e) => { const v = inputValue(e).trim(); set('review_ceiling', v === '' ? null : Number(v)); }} />
          </Field>
          <Field label="Review size band (low – high)">
            <div class="row">
              <input type="number" min={0} value={p.size_band_reviews[0]} style="width:50%" onInput={(e) => set('size_band_reviews', [Number(inputValue(e)), p.size_band_reviews[1]])} />
              <input type="number" min={0} value={p.size_band_reviews[1]} style="width:50%" onInput={(e) => set('size_band_reviews', [p.size_band_reviews[0], Number(inputValue(e))])} />
            </div>
          </Field>
        </div>
        <div class="row" style="flex-wrap:wrap;gap:14px">
          <Check checked={p.emergency_trade} onChange={(v) => set('emergency_trade', v)} label="Emergency trade" />
          <Check checked={p.hours_norm.weekend_open_expected} onChange={(v) => set('hours_norm', { ...p.hours_norm, weekend_open_expected: v })} label="Weekend opening expected" />
          <Check checked={p.licence_required} onChange={(v) => set('licence_required', v)} label="Licence required (dispensaries)" />
          <Check checked={p.healthcare_ca_risk.flag} onChange={(v) => set('healthcare_ca_risk', { ...p.healthcare_ca_risk, flag: v })} label="Canadian healthcare risk (blocks Canada)" />
        </div>
        {p.healthcare_ca_risk.flag ? (
          <Field label="Healthcare reason"><input value={p.healthcare_ca_risk.reason} onInput={(e) => set('healthcare_ca_risk', { ...p.healthcare_ca_risk, reason: inputValue(e) })} /></Field>
        ) : null}
      </div>

      <div class="card lg-prof-sec">
        <h3>Offers, best first</h3>
        {p.offers_ranked.map((o, i) => (
          <div class="lg-list-row lg-offer-row">
            <span class="eyebrow">{i + 1}.</span>
            <b style="font-size:14px">{OFFER_LABELS[o.offer] ?? o.offer}</b>
            <input value={o.why} placeholder="Why this offer fits" aria-label={`Why ${OFFER_LABELS[o.offer] ?? o.offer}`}
              onInput={(e) => set('offers_ranked', p.offers_ranked.map((x, j) => (j === i ? { ...x, why: inputValue(e) } : x)))} />
            <div class="row">
              <button type="button" class="btn btn-sm" disabled={i === 0} onClick={() => moveOffer(i, -1)} aria-label="Move up">↑</button>
              <button type="button" class="btn btn-sm" disabled={i === p.offers_ranked.length - 1} onClick={() => moveOffer(i, 1)} aria-label="Move down">↓</button>
              <button type="button" class="btn btn-sm" onClick={() => set('offers_ranked', p.offers_ranked.filter((_, j) => j !== i))}>Remove</button>
            </div>
          </div>
        ))}
        {unusedOffers.length ? (
          <div class="row">{unusedOffers.map((o) => <button type="button" class="btn btn-sm" onClick={() => set('offers_ranked', [...p.offers_ranked, { offer: o, why: '' }])}>+ {OFFER_LABELS[o]}</button>)}</div>
        ) : null}
      </div>

      <div class="card lg-prof-sec">
        <h3>Lists</h3>
        {CHIP_FIELDS.map((f) => (
          <Field label={`${f.label} (${(p[f.key] ?? []).length})`} hint={f.hint}>
            <ChipEditor label={f.label} values={p[f.key] ?? []} onChange={(v) => set(f.key, v)} />
          </Field>
        ))}
      </div>

      <div class="card lg-prof-sec">
        <h3>Chain brands <span class="lg-note">({p.chain_brands.filter((b) => !b.verified).length} to confirm)</span></h3>
        <div class="lg-note">Click a brand to confirm it, × to remove it. Unconfirmed brands are marked “to confirm”.</div>
        <div class="lg-chips">
          {p.chain_brands.map((b, i) => (
            <span class={`chip ${b.verified ? 'chip-ok' : 'chip-warn'}`}>
              <button type="button" class="lg-chip-x" title={b.verified ? 'Confirmed. Click to unconfirm.' : 'To confirm. Click once you have checked it.'}
                onClick={() => set('chain_brands', p.chain_brands.map((x, j) => (j === i ? { ...x, verified: !x.verified } : x)))}>
                {b.verified ? '✓' : '?'} {b.name}{b.verified ? '' : ' (to confirm)'}
              </button>
              <button type="button" class="lg-chip-x" aria-label={`Remove ${b.name}`} onClick={() => set('chain_brands', p.chain_brands.filter((_, j) => j !== i))}>×</button>
            </span>
          ))}
          <ChipEditor label="Add chain brands" values={[]} placeholder="Add a brand, press Enter"
            onChange={(added) => set('chain_brands', [...p.chain_brands, ...added.filter((a) => !p.chain_brands.some((b) => b.name.toLowerCase() === a.toLowerCase())).map((name) => ({ name, verified: true }))])} />
        </div>
      </div>

      <div class="card lg-prof-sec">
        <h3>Software vendors <span class="lg-note">({p.software_vendors.filter((v) => !v.verified).length} unverified)</span></h3>
        {p.software_vendors.map((v, i) => {
          const upd = (next: Partial<typeof v>) => set('software_vendors', p.software_vendors.map((x, j) => (j === i ? { ...x, ...next } : x)));
          return (
            <div class="lg-list-row lg-vendor-row">
              <input value={v.name} placeholder="Name" aria-label="Vendor name" onInput={(e) => upd({ name: inputValue(e) })} />
              <input value={v.host} placeholder="host, e.g. app.example.com" aria-label="Vendor host" class="mono" onInput={(e) => upd({ host: inputValue(e).trim().toLowerCase() })} />
              <select value={v.effect} aria-label="Effect" onChange={(e) => upd({ effect: inputValue(e) as typeof v.effect })}>{VENDOR_EFFECTS.map((x) => <option value={x.value}>{x.label}</option>)}</select>
              <Check checked={v.verified} onChange={(ok) => upd({ verified: ok })} label={v.verified ? 'verified' : 'unverified'} />
              <button type="button" class="btn btn-sm" onClick={() => set('software_vendors', p.software_vendors.filter((_, j) => j !== i))}>Remove</button>
            </div>
          );
        })}
        <div><button type="button" class="btn btn-sm" onClick={() => set('software_vendors', [...p.software_vendors, { name: '', host: '', effect: 'flag', verified: false }])}>+ Vendor</button></div>
      </div>

      <div class="card lg-prof-sec">
        <h3>Licence sources</h3>
        {p.licence_sources.length === 0 ? <div class="lg-note">None. Only licence niches (dispensaries) need one.</div> : null}
        {p.licence_sources.map((s, i) => {
          const upd = (next: Partial<typeof s>) => set('licence_sources', p.licence_sources.map((x, j) => (j === i ? { ...x, ...next } : x)));
          return (
            <div class="lg-list-row lg-licence-row">
              <input value={s.jurisdiction} placeholder="CA-ON" aria-label="Jurisdiction" onInput={(e) => upd({ jurisdiction: inputValue(e).trim().toUpperCase() })} />
              <select value={s.adapter} aria-label="Adapter" onChange={(e) => upd({ adapter: inputValue(e) as typeof s.adapter })}>{LICENCE_ADAPTERS.map((a) => <option value={a}>{a}</option>)}</select>
              <div class="row" style="min-width:0"><input value={s.url} placeholder="https://…" aria-label="Licence data URL" onInput={(e) => upd({ url: inputValue(e).trim() })} /><ExtLink href={/^https?:\/\//.test(s.url) ? s.url : null}>open</ExtLink></div>
              <button type="button" class="btn btn-sm" onClick={() => set('licence_sources', p.licence_sources.filter((_, j) => j !== i))}>Remove</button>
            </div>
          );
        })}
        <div><button type="button" class="btn btn-sm" onClick={() => set('licence_sources', [...p.licence_sources, { jurisdiction: '', adapter: 'none', url: '' }])}>+ Licence source</button></div>
      </div>
    </div>
  );
}
